/*
 * nd-qualif.js — questions de qualification des formulaires d'aimants (Neurodidacte)
 * ---------------------------------------------------------------------------------
 * Chargé UNIQUEMENT par les sections lead-magnet-registration et event-registration
 * quand leur réglage « Afficher les 2 questions de qualification » est coché.
 * Q1 est obligatoire, sauf si la liste porte `data-nd-qualif-facultatif`
 * (réglage « Question 1 facultative » d'event-registration, rencontres de la rentrée 2026).
 *
 * POURQUOI UN FICHIER SÉPARÉ
 * Le formulaire du guide est piloté par assets/nd-lead-capture.js, partagé avec le
 * webinaire, le guide des parents et l'article « dossier de remboursement ». Ce
 * module-là ne doit pas bouger d'un octet : on n'ajoute donc rien dedans. On se
 * place AVANT lui, sur l'événement `submit` en phase de capture au niveau du
 * document — il reçoit l'événement avant l'écouteur posé sur le <form>, quel que
 * soit le déclencheur (clic, touche Entrée, requestSubmit).
 *
 * CE QU'IL FAIT
 * 1. Ordre des erreurs = ordre visuel. Si le prénom est vide ou le courriel
 *    invalide, on ne fait RIEN : nd-lead-capture affiche son erreur et s'arrête
 *    avant d'envoyer quoi que ce soit. Sinon, si la situation (Q1) manque, on
 *    bloque l'envoi et on affiche l'erreur sous la liste.
 * 2. Horodatage. Si Q1 a une réponse, on remplit les champs cachés
 *    `last_qualification_form_name` et `last_qualification_at_ts` (secondes Unix)
 *    juste avant que nd-lead-capture récolte le formulaire.
 *
 * S'IL NE SE CHARGE PAS (bloqueur, erreur réseau) : le formulaire s'envoie comme
 * aujourd'hui, sans horodatage. Aucune erreur n'est levée hors des try/catch.
 */
(function () {
  'use strict';

  if (window.ndQualif) return;

  // Même expression que nd-lead-capture.js (isValidEmail) : les deux doivent juger pareil.
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var ERROR_TEXT = 'Choisis la situation qui te décrit le mieux.';

  function situationOf(root) {
    return root.querySelector('[data-nd-qualif-situation]');
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function clearError(root) {
    var select = situationOf(root);
    if (!select) return;
    var field = select.closest('[data-nd-qualif-field]');
    if (field) {
      field.classList.remove('nd-field--error');
      var existing = field.querySelector('[data-nd-qualif-error]');
      if (existing) existing.remove();
    }
    select.removeAttribute('aria-invalid');
    select.removeAttribute('aria-describedby');
  }

  function showError(root) {
    var select = situationOf(root);
    if (!select) return;
    clearError(root);
    var field = select.closest('[data-nd-qualif-field]');
    var span = document.createElement('span');
    span.className = 'nd-field__error';
    span.id = select.id + '-error';
    span.setAttribute('data-nd-qualif-error', '');
    span.setAttribute('role', 'alert');
    span.textContent = ERROR_TEXT;
    if (field) {
      field.classList.add('nd-field--error');
      field.appendChild(span);
    }
    select.setAttribute('aria-invalid', 'true');
    select.setAttribute('aria-describedby', span.id);
    try {
      select.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      select.focus({ preventScroll: true });
    } catch (e) {
      select.focus();
    }
  }

  /* Retire les erreurs laissées par nd-lead-capture lors d'une tentative précédente.
     Il les efface au début de SON écouteur de soumission ; quand on bloque l'envoi pour Q1,
     cet écouteur ne tourne pas, d'où ce nettoyage (même logique que son clearFieldError).
     Prénom et courriel viennent de passer les mêmes tests : les effacer est toujours juste. */
  function clearStaleErrors(form, fields) {
    fields.forEach(function (el) {
      if (!el) return;
      var field = el.closest('.nd-field') || el.parentNode;
      if (!field || field.hasAttribute('data-nd-qualif-field')) return;
      field.classList.remove('nd-field--error');
      var existing = field.querySelector('.nd-field__error:not([data-nd-qualif-error])');
      if (existing) existing.remove();
    });
    var box = form.querySelector('.nd-form__error');
    if (box) box.hidden = true;
  }

  /* Q1 a-t-elle une réponse ? Affiche ou retire l'erreur. Sans liste : rien à valider.
     Q1 facultative (attribut posé par le snippet quand la section le demande) : jamais bloquante ;
     stamp() n'horodate de toute façon qu'une réponse réelle. */
  function validate(root) {
    var select = situationOf(root);
    if (!select) return true;
    if (select.value || select.hasAttribute('data-nd-qualif-facultatif')) {
      clearError(root);
      return true;
    }
    showError(root);
    return false;
  }

  /* Remplit les 2 champs cachés — seulement quand Q1 a une réponse. */
  function stamp(root) {
    var select = situationOf(root);
    if (!select || !select.value) return;
    var source = root.querySelector('[data-nd-qualif-source]');
    var ts = root.querySelector('[data-nd-qualif-ts]');
    if (source) source.value = root.getAttribute('data-nd-qualif-form-name') || '';
    if (ts) ts.value = String(Math.floor(Date.now() / 1000));
  }

  function onSubmit(e) {
    try {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var root = form.querySelector('[data-nd-qualif][data-nd-qualif-mode="intercept"]');
      if (!root) return;

      // Prénom / courriel d'abord : on laisse nd-lead-capture signaler ses propres erreurs.
      var prenom = form.querySelector('[name="contact[prenom]"]');
      var email = form.querySelector('[name="contact[email]"]');
      if (prenom && !prenom.value.trim()) return;
      if (email && !EMAIL.test(email.value.trim())) return;

      if (!validate(root)) {
        clearStaleErrors(form, [prenom, email, form.querySelector('[data-nd-consent]')]);
        e.preventDefault();
        // stopPropagation et non stopImmediatePropagation : les autres écouteurs du
        // document restent servis ; seul l'écouteur du <form> (nd-lead-capture) est évité.
        e.stopPropagation();
        return;
      }
      stamp(root);
    } catch (err) {
      /* en cas d'imprévu, on n'empêche jamais l'envoi */
    }
  }

  function onChange(e) {
    var t = e.target;
    if (!t || !t.matches || !t.matches('[data-nd-qualif-situation]') || !t.value) return;
    var root = t.closest('[data-nd-qualif]');
    if (root) clearError(root);
  }

  // Écouteurs au niveau du document : fonctionnent aussi après un rechargement
  // de section dans l'éditeur de thème, sans réinitialisation.
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('change', onChange, true);

  window.ndQualif = { validate: validate, stamp: stamp };
})();
